import { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Post } from '../../shared/types/post';
import { getUserPostsPath } from '../../shared/config/env';
import { getI18nStrings } from '../../shared/data/i18n';
import { getPublicTags } from '../../shared/api/tags';
import { Tag } from '../../shared/api/tags';
import './PostCard.css';

interface PostCardProps {
  post: Post;
  lang: 'ja' | 'en';
}

function PostCard({ post, lang }: PostCardProps) {
    const [imageError, setImageError] = useState<boolean>(false);
    const [tags, setTags] = useState<Tag[]>([]);
    const navigate = useNavigate();
    const userPostsPath = getUserPostsPath();
    const i18n = getI18nStrings(lang);
    
    // タグ一覧を取得（slugの取得のために必要）
    useEffect(() => {
        const fetchTags = async () => {
            try {
                const response = await getPublicTags();
                setTags(response.tags);
            } catch (err) {
                // タグ取得エラーは無視（オプショナル）
            }
        };
        fetchTags();
    }, []);
    
    // カテゴリー名を取得（Get-posts APIから既に適切な言語のテキストが返されているため、直接使用）
    const getCategoryName = useMemo(() => {
        if (!post.category) return null;
        
        // 空白のみのカテゴリーは無効として扱う
        if (!post.category.trim()) return null;
        
        // Get-posts APIから既に適切な言語のテキストが返されているため、そのまま使用
        return post.category.trim();
    }, [post.category]);
    
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return i18n.postDetail.dateOnlyFormat(year, month, day);
    };

    // サムネイルURLを検証（実在しないURLや外部サービスのURLは除外）
    // TODO: サムネイル画像の保管機能を実装したら、このチェックを削除または調整する
    // - 実在する画像URLが設定されるようになったら、example.com や via.placeholder.com の除外チェックを削除
    // - または、実在する画像URLのホワイトリスト方式に変更する
    const isValidThumbnailUrl = useMemo(() => {
        if (!post.thumbnail_url) return false;
        
        let url = post.thumbnail_url.trim();
        
        // 空文字列の場合は無効
        if (!url) return false;
        
        // URLが完全な形式（http://またはhttps://で始まる）かチェック
        let fullUrl: string;
        if (url.startsWith('http://') || url.startsWith('https://')) {
            fullUrl = url;
        } else if (url.startsWith('//')) {
            fullUrl = `https:${url}`;
        } else if (url.includes('.')) {
            fullUrl = `https://${url}`;
        } else {
            return false;
        }
        
        try {
            const urlObj = new URL(fullUrl);
            
            // プロトコルがhttpまたはhttpsでない場合は無効
            if (urlObj.protocol !== 'http:' && urlObj.protocol !== 'https:') {
                return false;
            }
            
            // TODO: サムネイル画像保管機能実装後、このチェックを削除
            // example.com のような実在しないドメインは除外
            if (urlObj.hostname === 'example.com' || urlObj.hostname.endsWith('.example.com')) {
                return false;
            }
            
            // TODO: サムネイル画像保管機能実装後、このチェックを削除
            // via.placeholder.com のような外部サービスも除外（実在しないURLの可能性があるため）
            if (urlObj.hostname === 'via.placeholder.com') {
                return false;
            }
            
            return true;
        } catch {
            return false;
        }
    }, [post.thumbnail_url]);

    const handleImageError = () => {
        setImageError(true);
    };

    // タグクリック時の処理
    const handleTagClick = (e: React.MouseEvent<HTMLSpanElement>, tagSlug: string) => {
        e.preventDefault();
        e.stopPropagation();
        navigate(`/${lang}?tag=${encodeURIComponent(tagSlug)}`);
    };

    // 表示するサムネイルURLを決定
    // 実在しないURLや外部サービスのURLは表示しない
    const displayThumbnailUrl = isValidThumbnailUrl && !imageError && post.thumbnail_url
        ? (() => {
            let url = post.thumbnail_url.trim();
            if (url.startsWith('http://') || url.startsWith('https://')) {
                return url;
            } else if (url.startsWith('//')) {
                return `https:${url}`;
            } else if (url.includes('.')) {
                return `https://${url}`;
            }
            return null;
        })()
        : null;

    return (
        <article className="post-card">
            {displayThumbnailUrl && (
                <div className='post-card__thumbnail'>
                    <img
                        src={displayThumbnailUrl}
                        alt={post.title}
                        className='post-card__thumbnail-image'
                        onError={handleImageError}
                    />
                </div>
            )}
            <div className='post-card__content'>
                {getCategoryName && (
                    <div className='post-card__category'>{getCategoryName}</div>
                )}
                <h2 className='post-card__title'>
                  {post.title}
                </h2>
                <div className='post-card__meta'>
                    <span className='post-card__date'>{post.published_at ? formatDate(post.published_at) : ''}</span>
                </div>
                {post.excerpt && (
                    <p className='post-card__excerpt'>
                      {post.excerpt}
                    </p>
                )}
                {post.tags && post.tags.length > 0 && (
                    <div className="post-card__tags">
                        {post.tags.map((tag, index) => {
                            // tag_slugsが存在する場合はslugを使用（優先）
                            // ない場合はタグ一覧から検索してslugを取得（クリック時のナビゲーションに必要）
                            let tagSlug: string;
                            if (post.tag_slugs && post.tag_slugs[index] && post.tag_slugs[index].trim()) {
                                tagSlug = post.tag_slugs[index].trim();
                            } else {
                                // tag_slugsが存在しない場合、タグ一覧から検索
                                // まず日本語名で検索
                                let foundTag = tags.find(t => t.name === tag);
                                // 見つからない場合は英語名で検索
                                if (!foundTag) {
                                    foundTag = tags.find(t => t.nameEn === tag);
                                }
                                // タグ情報が見つかった場合はslugを使用、ない場合はフォールバック
                                tagSlug = foundTag ? foundTag.slug : tag.toLowerCase().replace(/\s+/g, '-');
                            }
                            
                            // Get-posts APIから既に適切な言語のテキストが返されているため、そのまま使用
                            const displayTagName = tag;
                            
                            return (
                                <span
                                    key={`${tagSlug}-${index}`}
                                    className="post-card__tag post-card__tag--clickable"
                                    onClick={(e) => handleTagClick(e, tagSlug)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleTagClick(e as any, tagSlug);
                                        }
                                    }}
                                    aria-label={lang === 'ja' ? `${displayTagName}タグでフィルター` : `Filter by ${displayTagName} tag`}
                                >
                                    {displayTagName}
                                </span>
                            );
                        })}
                    </div>
                )}
                <Link to={`/${lang}${userPostsPath}/${post.slug}`} className='post-card__link'>
                  {lang === 'ja' ? '続きを読む' : 'Read more'}
                </Link>
            </div>
        </article>
    );
}

export default PostCard;